import { FC } from 'react';
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';

import { SaveBlockReason } from '../../selectors/workflow-selectors';

interface DomainValidationBannerProps {
  /**
   * The reason save / navigation is currently blocked. When null, nothing renders.
   * For a generic validation error string, pass `state="validation-error"` and the
   * message via `validationError`.
   */
  state: SaveBlockReason | null;
  /**
   * Free-text validation message (e.g. "min/max documents not satisfied").
   * Only rendered when `state === 'validation-error'`.
   */
  validationError?: string | null;
}

/**
 * Single-banner display for the "why is Save blocked?" state. Driven by
 * `getSaveBlockReason` — see workflow-selectors.ts for the precedence rules.
 *
 * Color/severity is chosen here: anything user-fixable-by-waiting is warning,
 * anything that requires a corrective action is error.
 */
export const DomainValidationBanner: FC<DomainValidationBannerProps> = observer(({
  state,
  validationError,
}) => {
  if (!state) return null;

  if (state === 'uploading') {
    return (
      <Typography
        variant="textSM_regular"
        sx={{ color: 'warning.main', display: 'block', mb: 1.5 }}
      >
        <Trans
          i18nKey="app.assessment.workflow.validation.waitForUploads"
          defaults="Please wait for all files to finish uploading before saving."
        />
      </Typography>
    );
  }

  if (state === 'validation-error') {
    if (!validationError) return null;
    return (
      <Typography
        variant="textSM_regular"
        sx={{ color: 'error.main', display: 'block', mb: 1.5 }}
      >
        {validationError}
      </Typography>
    );
  }

  if (state === 'classifying') {
    return (
      <Typography
        variant="textSM_regular"
        sx={{ color: 'warning.main', display: 'block', mb: 1.5 }}
      >
        <Trans
          i18nKey="app.assessment.workflow.validation.classificationInProgress"
          defaults="Documents are being classified. Please wait before saving."
        />
      </Typography>
    );
  }

  if (state === 'classification-failed') {
    return (
      <Typography
        variant="textSM_regular"
        sx={{ color: 'warning.main', display: 'block', mb: 1.5 }}
      >
        <Trans
          i18nKey="app.assessment.workflow.validation.classificationFailed"
          defaults="Some documents failed to classify. Please retry or remove them before saving."
        />
      </Typography>
    );
  }

  return (
    <Typography
      variant="textSM_regular"
      sx={{ color: 'error.main', display: 'block', mb: 1.5 }}
    >
      <Trans
        i18nKey="app.assessment.workflow.validation.noValidClassification"
        defaults="All uploaded documents are invalid. Please upload at least one document with a valid classification (medium or high confidence), or remove all invalid documents and skip this domain."
      />
    </Typography>
  );
});
